import React from 'react';
import { Select } from 'antd';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';

export interface IdOption {
    label: string;
    value: number;
}

type LabeledValue = { value: string | number; label?: React.ReactNode };

const normalizeSelections = (values: LabeledValue[]): IdOption[] => {
    const unique = new Map<number, IdOption>();
    values.forEach(item => {
        const v = Number(item.value);
        if (!unique.has(v)) {
            unique.set(v, {
                value: v,
                label: typeof item.label === 'string'
                    ? item.label
                    : item.label !== undefined
                        ? String(item.label)
                        : String(item.value)
            });
        }
    });
    return Array.from(unique.values());
};

const toSelectValues = (options?: IdOption[]): LabeledValue[] =>
    options?.map(opt => ({ value: opt.value, label: opt.label })) ?? [];

interface MultiIdSelectFilterItemProps {
    label: string;
    options: IdOption[];
    value?: IdOption[];
    onChange?: (next?: IdOption[]) => void;
    className?: string;
    spanClassName?: string;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
}

const MultiIdSelectFilterItem: React.FC<MultiIdSelectFilterItemProps> = ({
    label,
    options,
    value,
    onChange,
    className,
    spanClassName,
    placeholder,
    loading,
    disabled
}) => {
    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    mode="multiple"
                    labelInValue
                    allowClear
                    showSearch
                    placeholder={placeholder}
                    value={toSelectValues(value)}
                    onChange={(val) => {
                        const next = Array.isArray(val) ? normalizeSelections(val as LabeledValue[]) : [];
                        onChange?.(next.length > 0 ? next : undefined);
                    }}
                    options={options}
                    loading={loading}
                    disabled={disabled}
                    maxTagCount="responsive"
                    size="middle"
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                        String((option as any)?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>
        </div>
    );
};

export default MultiIdSelectFilterItem;

